import { useEffect, useRef, useState } from 'react';
import {
  Users as UsersIcon, Plus, Upload, Download, Search, Pencil, Trash2, FileDown,
  TrendingUp, UserCheck, UserPlus, UserX, Radio,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listUsers, createUser, updateUser, deleteUser, exportUsers,
  downloadImportTemplate, getUserStats, importUsersChunked,
  type AdminUser, type AdminUserStats, type ImportProgress, type ImportFinalResult,
} from '../services/admin_api';
import { EmailQueueDashboard } from '../components/EmailQueueDashboard';
import { extractErrorMessage } from '@/services/api';

// Users page: admin CRUD + bulk Excel import/export for the
// subscription-driven user list.
const PAGE_SIZE = 10;

export function Users() {
  const [items, setItems] = useState<AdminUser[]>([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [importResult, setImportResult] = useState<ImportFinalResult | null>(null);
  const [stats, setStats] = useState<AdminUserStats | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const importAbortRef = useRef<AbortController | null>(null);

  async function refresh(p = page) {
    setLoading(true);
    try {
      const rows = await listUsers(q, PAGE_SIZE + 1, p * PAGE_SIZE);
      const hasMore = rows.length > PAGE_SIZE;
      setItems(hasMore ? rows.slice(0, PAGE_SIZE) : rows);
      setTotal(p * PAGE_SIZE + rows.length);
    }
    catch (e) { toast.error(extractErrorMessage(e, 'Load failed')); }
    finally { setLoading(false); }
  }

  async function refreshStats() {
    try {
      const s = await getUserStats();
      setStats(s);
    } catch {
      // Non-fatal — cards just stay blank.
    }
  }

  useEffect(() => { refresh(0); setPage(0); refreshStats(); /* eslint-disable-next-line */ }, []);

  // Live activity counters drift second-by-second; poll every 15s so the
  // "Live now" card stays fresh without page reload. Cleared on unmount.
  useEffect(() => {
    const id = setInterval(refreshStats, 15_000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  // Debounced search — reset to page 0 on new query.
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); refresh(0); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => { refresh(page); /* eslint-disable-next-line */ }, [page]);

  const onImport = async (f: File) => {
    setImporting(true);
    setImportResult(null);
    setImportProgress({
      batchIndex: 0, batchCount: 0,
      processedRows: 0, totalRows: 0,
      created: 0, skipped: 0, errors: [],
    });
    const ac = new AbortController();
    importAbortRef.current = ac;
    try {
      const res = await importUsersChunked(
        f,
        (p) => setImportProgress(p),
        { signal: ac.signal },
      );
      setImportResult(res);
      const parts = [
        `${res.created} created`,
        `${res.skipped} already existed`,
      ];
      if (res.errors.length) parts.push(`${res.errors.length} errors`);
      toast.success(`Import done — ${parts.join(' · ')}`);
      refresh();
      refreshStats();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Import failed'));
    } finally {
      setImporting(false);
      importAbortRef.current = null;
    }
  };

  const cancelImport = () => {
    importAbortRef.current?.abort();
  };

  const onExport = async () => {
    try {
      const blob = await exportUsers();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ira-users-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractErrorMessage(e, 'Export failed')); }
  };

  const onDownloadTemplate = async () => {
    try {
      const blob = await downloadImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ira-import-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractErrorMessage(e, 'Template download failed')); }
  };

  const onDelete = async (u: AdminUser) => {
    setConfirmDelete(u);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const u = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteUser(u.id);
      toast.success('Deleted');
      refresh();
    } catch (e) { toast.error(extractErrorMessage(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-4">
      {/* Floating email queue progress card — auto-appears during bulk
          import, auto-hides shortly after completion. */}
      <EmailQueueDashboard />

      {/* Live import progress — appears during upload, persists final
          summary until dismissed. Survives toast clutter on big runs. */}
      {(importing || importResult) && (
        <ImportProgressCard
          progress={importProgress}
          result={importResult}
          onCancel={importing ? cancelImport : undefined}
          onDismiss={() => { setImportResult(null); setImportProgress(null); }}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <UsersIcon className="h-6 w-6 text-primary" /> Users
          </h1>
          <p className="text-sm text-text-secondary">
            Add, edit, or bulk-import users. Passwords are bcrypt-hashed server-side.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost" onClick={onExport}>
            <Upload className="mr-1 h-4 w-4" /> Export
          </button>
          <button className="btn-ghost" onClick={onDownloadTemplate} title="Download blank import template">
            <FileDown className="mr-1 h-4 w-4" /> Template
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) onImport(f);
            }} />
          <button className="btn-ghost" disabled={importing} onClick={() => fileRef.current?.click()}>
            <Download className="mr-1 h-4 w-4" /> {importing ? 'Importing…' : 'Import Excel'}
          </button>
          <button className="btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add User
          </button>
        </div>
      </div>

      {/* Stats cards — admin sees true totals + LIVE activity at a glance.
          live_5min is sourced from Redis (touched on every authenticated
          request) so it reflects real "using-the-addin-right-now" count,
          not just "logged in recently". Auto-refreshes every 15s. */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatCard
            icon={<UsersIcon className="h-4 w-4" />}
            label="Total users"
            value={stats.total}
          />
          <StatCard
            icon={<Radio className="h-4 w-4" />}
            label="Live now"
            value={stats.live_5min}
            hint="last 5 min"
            accent="emerald"
            pulse
          />
          <StatCard
            icon={<UserCheck className="h-4 w-4" />}
            label="Active today"
            value={stats.live_activity_24h}
            hint={`${stats.live_activity_1h.toLocaleString()} in last hour`}
            accent="emerald"
          />
          <StatCard
            icon={<UserPlus className="h-4 w-4" />}
            label="Added today"
            value={stats.today_added}
            accent="blue"
          />
          <StatCard
            icon={<UserX className="h-4 w-4" />}
            label="Never logged in"
            value={stats.never_logged}
            hint="Imported, unused"
            accent="amber"
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
          <input className="input pl-8" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email…" />
        </div>
        <div className="text-xs text-text-secondary">
          {stats
            ? `${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + items.length} of ${stats.total.toLocaleString()}`
            : total > 0
              ? `${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + items.length} of ${total}`
              : '0 users'}
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/5 text-left text-xs uppercase text-text-secondary">
            <tr>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2 font-medium">Credits</th>
              <th className="px-3 py-2 font-medium">Subscription</th>
              <th className="px-3 py-2 font-medium">Last Login</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-6 text-center text-xs text-text-secondary">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="py-6 text-center text-xs text-text-secondary">
                {q ? `No users match "${q}".` : 'No users yet. Create one or import from Excel.'}
              </td></tr>
            ) : items.map((u) => (
              <tr key={u.id} className="border-t border-border/50">
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2">
                  <span className={[
                    'rounded-full px-2 py-0.5 text-[10px] uppercase',
                    u.role === 'admin' ? 'bg-accent-soft text-accent' : 'bg-black/5',
                  ].join(' ')}>{u.role}</span>
                </td>
                <td className="px-3 py-2">
                  {u.is_active ? (
                    <span className="text-green-700">yes</span>
                  ) : (
                    <span className="text-text-secondary">no</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs tabular-nums">
                  <span className={u.credits <= 0 ? 'text-red-500 font-medium' : u.credits <= 1000 ? 'text-amber-500' : ''}>
                    {u.credits?.toLocaleString() ?? '—'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.subscription_start ? shortDate(u.subscription_start) : '—'}
                  {' → '}
                  {u.subscription_end ? shortDate(u.subscription_end) : '∞'}
                </td>
                <td className="px-3 py-2 text-xs text-text-secondary">
                  {u.last_login_at ? shortDate(u.last_login_at) : '—'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button className="btn-ghost text-xs" onClick={() => setEditing(u)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button className="btn-ghost text-xs text-red-600 hover:bg-red-50" onClick={() => onDelete(u)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(page > 0 || items.length === PAGE_SIZE) && (
        <div className="flex items-center justify-between text-sm">
          <button
            className="btn-ghost"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Previous
          </button>
          <span className="text-xs text-text-secondary">Page {page + 1}</span>
          <button
            className="btn-ghost"
            disabled={items.length < PAGE_SIZE}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      <div className="card p-3 text-xs text-text-secondary">
        <div className="font-medium mb-1">Excel import format</div>
        <div>
          Columns (in order): <code className="font-mono">email</code>, <code className="font-mono">password</code>,{' '}
          <code className="font-mono">subscription_start</code>, <code className="font-mono">subscription_end</code>,{' '}
          <code className="font-mono">credits</code> (optional, default 10 000).
          {' '}Dates: <code>yyyy-mm-dd</code> or <code>dd-mm-yyyy</code>. Blank dates = no bound.
          {' '}Each created user receives a welcome email with their credentials.
        </div>
      </div>

      {createOpen && <UserModal onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); refresh(); }} />}
      {editing && <UserModal existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-elev" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold">Delete User</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Delete <span className="font-medium text-text-primary">{confirmDelete.email}</span>? This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn-primary bg-red-600 hover:bg-red-700" onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon, label, value, hint, accent, pulse,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  hint?: string;
  accent?: 'emerald' | 'blue' | 'amber';
  pulse?: boolean;
}) {
  const accentMap: Record<string, string> = {
    emerald: 'text-emerald-600 bg-emerald-50',
    blue: 'text-blue-600 bg-blue-50',
    amber: 'text-amber-600 bg-amber-50',
  };
  const accentCls = accent ? accentMap[accent] : 'text-text-secondary bg-black/5';
  return (
    <div className="card flex items-center gap-3 p-3">
      <div className={`relative flex h-9 w-9 items-center justify-center rounded-md ${accentCls}`}>
        {icon}
        {pulse && value > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-text-secondary">{label}</div>
        <div className="flex items-baseline gap-2">
          <div className="text-xl font-semibold tabular-nums">{value.toLocaleString()}</div>
          {hint && <div className="text-[11px] text-text-secondary">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function ImportProgressCard({
  progress, result, onCancel, onDismiss,
}: {
  progress: ImportProgress | null;
  result: ImportFinalResult | null;
  onCancel?: () => void;
  onDismiss: () => void;
}) {
  // Resolve the displayed counters preferring the final result when the
  // run has completed, otherwise the live progress snapshot.
  const total = result?.totalRows ?? progress?.totalRows ?? 0;
  const processed = result ? result.totalRows : progress?.processedRows ?? 0;
  const created = result?.created ?? progress?.created ?? 0;
  const skipped = result?.skipped ?? progress?.skipped ?? 0;
  const errors = result?.errors ?? progress?.errors ?? [];
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const done = !!result;
  const batchLine = result
    ? `Done in ${(result.durationMs / 1000).toFixed(1)}s`
    : progress
      ? `Batch ${progress.batchIndex}/${progress.batchCount}`
      : 'Preparing…';

  return (
    <div className="card border border-primary/30 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <TrendingUp className="h-4 w-4 text-primary" />
          {done ? 'Import complete' : 'Importing users…'}
        </div>
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <span>{batchLine}</span>
          {!done && onCancel && (
            <button className="btn-ghost text-xs text-red-600" onClick={onCancel}>
              Cancel
            </button>
          )}
          {done && (
            <button className="btn-ghost text-xs" onClick={onDismiss}>
              Dismiss
            </button>
          )}
        </div>
      </div>

      <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-black/5">
        <div
          className={`h-full rounded-full transition-all duration-200 ${done ? 'bg-emerald-500' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span><span className="font-medium">{processed.toLocaleString()}</span> / {total.toLocaleString()} rows</span>
        <span className="text-emerald-600"><span className="font-medium">{created.toLocaleString()}</span> created</span>
        <span className="text-amber-600"><span className="font-medium">{skipped.toLocaleString()}</span> already existed</span>
        {errors.length > 0 && (
          <span className="text-red-600"><span className="font-medium">{errors.length.toLocaleString()}</span> errors</span>
        )}
      </div>

      {errors.length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-text-secondary">
            View error details ({errors.length})
          </summary>
          <div className="mt-2 max-h-40 overflow-y-auto rounded border border-border/50 bg-black/5 p-2 font-mono">
            {errors.slice(0, 200).map((e, i) => (
              <div key={i} className="leading-tight">
                Row {e.row}: {e.reason}
              </div>
            ))}
            {errors.length > 200 && (
              <div className="mt-1 text-text-secondary">
                …and {errors.length - 200} more
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

function UserModal({
  existing, onClose, onSaved,
}: { existing?: AdminUser; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!existing;
  const [email, setEmail] = useState(existing?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>(existing?.role ?? 'user');
  const [isActive, setIsActive] = useState(existing?.is_active ?? true);
  const [start, setStart] = useState(existing?.subscription_start ? existing.subscription_start.slice(0, 10) : '');
  const [end, setEnd] = useState(existing?.subscription_end ? existing.subscription_end.slice(0, 10) : '');
  const [credits, setCredits] = useState<number>(existing?.credits ?? 10000);
  const [busy, setBusy] = useState(false);

  const CREDIT_OPTIONS = [3000, 5000, 10000, 20000, 100000];

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await updateUser(existing!.id, {
          is_active: isActive,
          new_password: password || undefined,
          subscription_start: start || undefined,
          subscription_end: end || undefined,
          credits,
        });
        toast.success('Saved');
      } else {
        await createUser({
          email: email.trim(), password, role, is_active: isActive,
          subscription_start: start || undefined,
          subscription_end: end || undefined,
          credits,
        });
        toast.success('Created');
      }
      onSaved();
    } catch (err) { toast.error(extractErrorMessage(err, 'Save failed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg bg-white p-5 shadow-elev" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold">{isEdit ? 'Edit User' : 'Add User'}</h3>
        <div className="mt-3 space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium">Email</span>
            <input className="input" type="email" required disabled={isEdit}
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium">
              {isEdit ? 'New Password (leave blank to keep)' : 'Password (min 8 chars)'}
            </span>
            <input className="input" type="password" minLength={isEdit ? 0 : 8}
              required={!isEdit}
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          {!isEdit && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium">Role</span>
              <select className="input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-xs font-medium">Credits</span>
            <select className="input" value={credits} onChange={(e) => setCredits(Number(e.target.value))}>
              {CREDIT_OPTIONS.map((n) => (
                <option key={n} value={n}>{n.toLocaleString()}</option>
              ))}
            </select>
            <p className="mt-0.5 text-[10px] text-text-secondary">1 credit ≈ 50 tokens · 10 000 credits ≈ 40 prompts</p>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium">Subscription Start</span>
              <input className="input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium">Subscription End</span>
              <input className="input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
}

function shortDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
